import { useState, useRef, useMemo } from 'react';
import { useApi } from '@/hooks/useApi';
import { apiFetch } from '@/lib/api';
import {
  Route,
  Plus,
  Download,
  Upload,
  Calculator,
  Car,
  User,
  MapPin,
  DollarSign,
  FileText,
  Loader2,
  ChevronDown,
  ChevronUp,
  X,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';

// ─── Types ───────────────────────────────────────────────────

type TripPurpose = 'business' | 'personal' | 'commute' | 'medical' | 'charity';

interface Trip {
  id: string;
  vehicle_id: string;
  vehicle_name: string;
  driver_id?: string;
  driver_name?: string;
  date: string;
  start_location?: string;
  end_location?: string;
  start_odometer?: number;
  end_odometer?: number;
  distance_miles: number;
  purpose: TripPurpose;
  business_purpose?: string;
  client_name?: string;
  billable: boolean;
  notes?: string;
  source: string;
}

interface TripListResponse {
  data: Trip[];
  total: number;
}

interface MileageSummary {
  period: { from: string; to: string };
  total_miles: number;
  business_miles: number;
  personal_miles: number;
  commute_miles: number;
  medical_miles: number;
  charity_miles: number;
  irs_rates: { business: number; medical: number; charity: number };
  irs_year: number;
  business_deduction_value: number;
  medical_deduction_value: number;
  charity_deduction_value: number;
  by_vehicle: { vehicle_id: string; name: string; miles: number; business_miles: number }[];
  by_driver: { driver_id: string; name: string; miles: number; business_miles: number }[];
}

// ─── Constants ───────────────────────────────────────────────

const PURPOSE_STYLES: Record<TripPurpose, string> = {
  business: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  personal: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
  commute: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  medical: 'bg-rose-500/10 text-rose-400 border-rose-500/30',
  charity: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
};

const PURPOSE_COLORS: Record<TripPurpose, string> = {
  business: '#10b981',
  personal: '#3b82f6',
  commute: '#f59e0b',
  medical: '#f43f5e',
  charity: '#a855f7',
};

const TABS = ['trips', 'mileage-report'] as const;
type Tab = (typeof TABS)[number];

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function monthStartStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

// ─── CSV helpers ─────────────────────────────────────────────

function downloadCsv(rows: string[][], filename: string) {
  const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function parseCsv(text: string): Record<string, string>[] {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = lines[0]!.split(',').map(h => h.trim().replace(/^"|"$/g, ''));
  return lines.slice(1).map(line => {
    const vals = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
    return Object.fromEntries(headers.map((h, i) => [h, vals[i] ?? '']));
  });
}

// ─── Log Trip Form ───────────────────────────────────────────

interface LogTripFormProps {
  onClose: () => void;
  onSaved: () => void;
  vehicleId?: string;
  vehicleName?: string;
}

function LogTripForm({ onClose, onSaved, vehicleId, vehicleName }: LogTripFormProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    vehicle_id: vehicleId ?? '',
    vehicle_name: vehicleName ?? '',
    driver_name: '',
    date: todayStr(),
    start_location: '',
    end_location: '',
    start_odometer: '',
    end_odometer: '',
    distance_miles: '',
    purpose: 'business' as TripPurpose,
    business_purpose: '',
    client_name: '',
    billable: false,
    billable_rate: '',
    notes: '',
  });

  const { data: vehicleList } = useApi<{ data: { id: string; name: string }[] }>('/api/vehicles?pageSize=200');
  const vehicles = vehicleList?.data ?? [];

  function handleOdometer(field: 'start_odometer' | 'end_odometer', val: string) {
    const updated = { ...form, [field]: val };
    const start = parseFloat(updated.start_odometer);
    const end = parseFloat(updated.end_odometer);
    if (!isNaN(start) && !isNaN(end) && end > start) {
      updated.distance_miles = String(Math.round((end - start) * 10) / 10);
    }
    setForm(updated);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await apiFetch('/api/trips', {
        method: 'POST',
        body: JSON.stringify({
          vehicle_id: form.vehicle_id,
          vehicle_name: form.vehicle_name,
          driver_name: form.driver_name || undefined,
          date: form.date,
          start_location: form.start_location || undefined,
          end_location: form.end_location || undefined,
          start_odometer: form.start_odometer ? parseFloat(form.start_odometer) : undefined,
          end_odometer: form.end_odometer ? parseFloat(form.end_odometer) : undefined,
          distance_miles: parseFloat(form.distance_miles),
          purpose: form.purpose,
          business_purpose: form.purpose === 'business' ? form.business_purpose : undefined,
          client_name: form.client_name || undefined,
          billable: form.billable,
          billable_rate: form.billable_rate ? parseFloat(form.billable_rate) : undefined,
          notes: form.notes || undefined,
          source: 'manual',
        }),
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save trip');
    } finally {
      setSaving(false);
    }
  }

  const inputCls = 'w-full rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-emerald-500';
  const labelCls = 'block text-xs font-medium text-zinc-400 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl border border-zinc-700 bg-zinc-900 shadow-2xl">
        <div className="flex items-center justify-between border-b border-zinc-800 px-6 py-4">
          <h2 className="text-lg font-semibold text-zinc-100">Log Trip</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Vehicle */}
            <div className="col-span-2 sm:col-span-1">
              <label className={labelCls}>Vehicle *</label>
              {vehicleId ? (
                <input className={inputCls} value={form.vehicle_name} disabled />
              ) : (
                <select
                  className={inputCls}
                  required
                  value={form.vehicle_id}
                  onChange={e => {
                    const v = vehicles.find(x => x.id === e.target.value);
                    setForm(f => ({ ...f, vehicle_id: e.target.value, vehicle_name: v?.name ?? '' }));
                  }}
                >
                  <option value="">Select vehicle...</option>
                  {vehicles.map(v => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Date */}
            <div>
              <label className={labelCls}>Date *</label>
              <input type="date" className={inputCls} required value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            </div>

            {/* Driver */}
            <div>
              <label className={labelCls}>Driver</label>
              <input className={inputCls} placeholder="Driver name" value={form.driver_name}
                onChange={e => setForm(f => ({ ...f, driver_name: e.target.value }))} />
            </div>

            {/* Start location */}
            <div>
              <label className={labelCls}>Start Location</label>
              <input className={inputCls} placeholder="e.g. Office" value={form.start_location}
                onChange={e => setForm(f => ({ ...f, start_location: e.target.value }))} />
            </div>

            {/* End location */}
            <div>
              <label className={labelCls}>End Location</label>
              <input className={inputCls} placeholder="e.g. Client site" value={form.end_location}
                onChange={e => setForm(f => ({ ...f, end_location: e.target.value }))} />
            </div>

            {/* Odometers */}
            <div>
              <label className={labelCls}>Start Odometer (mi)</label>
              <input type="number" className={inputCls} placeholder="Optional" value={form.start_odometer}
                onChange={e => handleOdometer('start_odometer', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>End Odometer (mi)</label>
              <input type="number" className={inputCls} placeholder="Optional" value={form.end_odometer}
                onChange={e => handleOdometer('end_odometer', e.target.value)} />
            </div>

            {/* Distance */}
            <div>
              <label className={labelCls}>Distance (miles) *</label>
              <input type="number" step="0.1" className={inputCls} required placeholder="e.g. 24.5"
                value={form.distance_miles}
                onChange={e => setForm(f => ({ ...f, distance_miles: e.target.value }))} />
            </div>

            {/* Purpose */}
            <div>
              <label className={labelCls}>Purpose *</label>
              <select className={inputCls} value={form.purpose}
                onChange={e => setForm(f => ({ ...f, purpose: e.target.value as TripPurpose, billable: e.target.value === 'business' ? f.billable : false }))}>
                <option value="business">Business</option>
                <option value="personal">Personal</option>
                <option value="commute">Commute</option>
                <option value="medical">Medical</option>
                <option value="charity">Charity</option>
              </select>
            </div>

            {/* Business purpose — required for IRS */}
            {form.purpose === 'business' && (
              <div className="col-span-2">
                <label className={labelCls}>
                  Business Purpose *
                  <span className="ml-1 text-zinc-500 font-normal">(IRS requires specific description)</span>
                </label>
                <input className={inputCls} required placeholder="e.g. Client site visit — Acme Corp quarterly review"
                  value={form.business_purpose}
                  onChange={e => setForm(f => ({ ...f, business_purpose: e.target.value }))} />
              </div>
            )}

            {/* Client + billable (only for business) */}
            {form.purpose === 'business' && (
              <>
                <div>
                  <label className={labelCls}>Client (optional)</label>
                  <input className={inputCls} placeholder="Client name" value={form.client_name}
                    onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} />
                </div>
                <div className="flex items-end gap-3">
                  <label className="flex items-center gap-2 cursor-pointer mt-4">
                    <input type="checkbox" checked={form.billable}
                      onChange={e => setForm(f => ({ ...f, billable: e.target.checked }))}
                      className="rounded border-zinc-600 bg-zinc-800 text-emerald-500" />
                    <span className="text-sm text-zinc-300">Billable to client</span>
                  </label>
                </div>
                {form.billable && (
                  <div>
                    <label className={labelCls}>Billable Rate ($/mile)</label>
                    <input type="number" step="0.01" className={inputCls} placeholder="e.g. 0.70"
                      value={form.billable_rate}
                      onChange={e => setForm(f => ({ ...f, billable_rate: e.target.value }))} />
                  </div>
                )}
              </>
            )}

            {/* Notes */}
            <div className="col-span-2">
              <label className={labelCls}>Notes</label>
              <textarea rows={2} className={inputCls} placeholder="Optional notes" value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>

          {error && <p className="text-sm text-rose-400">{error}</p>}

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving}
              className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50 transition-colors">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Trip
            </button>
            <button type="button" onClick={onClose}
              className="rounded-lg border border-zinc-700 px-4 py-2 text-sm text-zinc-400 hover:text-zinc-100 transition-colors">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Import Modal ─────────────────────────────────────────────

interface ImportModalProps {
  onClose: () => void;
  onSaved: () => void;
}

const CSV_TEMPLATE_HEADERS = ['date', 'vehicle_name', 'driver_name', 'distance_miles', 'purpose', 'business_purpose', 'notes'];
const CSV_TEMPLATE_EXAMPLE = ['2026-03-09', 'Fleet Truck 1', 'Jane Smith', '42.5', 'business', 'Client visit - Acme Corp', ''];

function ImportModal({ onClose, onSaved }: ImportModalProps) {
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ imported: number; errors: { row: number; reason: string }[] } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const parsed = parseCsv(String(evt.target?.result ?? ''));
      setRows(parsed.slice(0, 5));
    };
    reader.readAsText(file);
  }

  async function handleImport() {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    const text = await file.text();
    const parsed = parseCsv(text);

    setImporting(true);
    try {
      const payload = parsed.map(r => ({
        date: r.date,
        vehicle_name: r.vehicle_name,
        driver_name: r.driver_name || undefined,
        distance_miles: parseFloat(r.distance_miles ?? '0'),
        purpose: r.purpose as TripPurpose,
        business_purpose: r.business_purpose || undefined,
        notes: r.notes || undefined,
      }));

      const res = await apiFetch<{ imported: number; errors: { row: number; reason: string }[] }>(
        '/api/trips/import',
        { method: 'POST', body: JSON.stringify(payload) },
      );
      setResult(res);
      if (res.errors.length === 0) {
        onSaved();
      }
    } catch (err) {
      setResult({ imported: 0, errors: [{ row: 0, reason: String(err) }] });
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-xl rounded-xl border border-zinc-700 bg-zinc-900 shadow-2xl">
        <div className="flex items-center justify-between border-b border-zinc-800 px-6 py-4">
          <h2 className="text-lg font-semibold text-zinc-100">Import Trips from CSV</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          <button
            onClick={() => downloadCsv([CSV_TEMPLATE_HEADERS, CSV_TEMPLATE_EXAMPLE], 'trip-import-template.csv')}
            className="flex items-center gap-2 text-sm text-emerald-400 hover:text-emerald-300"
          >
            <Download className="h-4 w-4" /> Download CSV template
          </button>

          <div>
            <label className="block text-xs font-medium text-zinc-400 mb-1">Upload CSV file</label>
            <input ref={fileRef} type="file" accept=".csv" onChange={handleFile}
              className="block text-sm text-zinc-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-zinc-700 file:text-zinc-200 file:text-sm hover:file:bg-zinc-600" />
          </div>

          {rows.length > 0 && (
            <div>
              <p className="text-xs text-zinc-500 mb-2">Preview (first {rows.length} rows):</p>
              <div className="overflow-x-auto rounded-lg border border-zinc-700">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-zinc-700 bg-zinc-800/50">
                      {Object.keys(rows[0]!).map(h => (
                        <th key={h} className="px-3 py-2 text-left text-zinc-400">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className="border-b border-zinc-800">
                        {Object.values(r).map((v, j) => (
                          <td key={j} className="px-3 py-2 text-zinc-300">{v}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result && (
            <div className={`rounded-lg p-3 text-sm ${result.errors.length === 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>
              <p className="font-medium">Imported {result.imported} trips</p>
              {result.errors.map((e, i) => (
                <p key={i} className="text-xs mt-1">Row {e.row}: {e.reason}</p>
              ))}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button onClick={handleImport} disabled={importing || !fileRef.current?.files?.length}
              className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50 transition-colors">
              {importing && <Loader2 className="h-4 w-4 animate-spin" />}
              Import
            </button>
            <button onClick={onClose}
              className="rounded-lg border border-zinc-700 px-4 py-2 text-sm text-zinc-400 hover:text-zinc-100">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export default function TripsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('trips');
  const [showLog, setShowLog] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Filters
  const [filterPurpose, setFilterPurpose] = useState('');
  const [filterFrom, setFilterFrom] = useState(monthStartStr());
  const [filterTo, setFilterTo] = useState(todayStr());

  // Mileage report filters
  const [reportFrom, setReportFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [reportTo, setReportTo] = useState(todayStr());
  const [reportPreset, setReportPreset] = useState('ytd');

  const tripParams = useMemo(() => {
    const p = new URLSearchParams({ pageSize: '200' });
    if (filterFrom) p.set('from', filterFrom);
    if (filterTo) p.set('to', filterTo);
    if (filterPurpose) p.set('purpose', filterPurpose);
    return p.toString();
  }, [filterFrom, filterTo, filterPurpose, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const reportParams = useMemo(() => {
    const p = new URLSearchParams({ from: reportFrom, to: reportTo });
    return p.toString();
  }, [reportFrom, reportTo]);

  const { data: tripData, isLoading: tripsLoading, refetch: refetchTrips } = useApi<TripListResponse>(`/api/trips?${tripParams}`);
  const { data: summary, isLoading: summaryLoading } = useApi<MileageSummary>(
    activeTab === 'mileage-report' ? `/api/trips/mileage-summary?${reportParams}` : null,
  );

  const trips = tripData?.data ?? [];

  // This-month summary cards (use filter range)
  const { data: monthSummary } = useApi<MileageSummary>(
    `/api/trips/mileage-summary?from=${filterFrom}&to=${filterTo}`,
  );

  function handleSaved() {
    setRefreshKey(k => k + 1);
    refetchTrips();
  }

  function exportTripsCsv() {
    const headers = ['Date', 'Vehicle', 'Driver', 'Start', 'End', 'Miles', 'Purpose', 'Business Purpose', 'Client', 'Billable', 'Notes'];
    const rows = trips.map(t => [
      t.date, t.vehicle_name, t.driver_name ?? '', t.start_location ?? '', t.end_location ?? '',
      String(t.distance_miles), t.purpose, t.business_purpose ?? '', t.client_name ?? '',
      t.billable ? 'Yes' : 'No', t.notes ?? '',
    ]);
    downloadCsv([headers, ...rows], `trips-${filterFrom}-${filterTo}.csv`);
  }

  function exportMileageCsv() {
    if (!summary) return;
    const rows = [
      ['IRS Mileage Report', `${summary.period.from} to ${summary.period.to}`],
      [],
      ['Category', 'Miles', 'Rate', 'Deduction'],
      ['Business', String(summary.business_miles), `$${summary.irs_rates.business}/mile`, `$${summary.business_deduction_value.toFixed(2)}`],
      ['Medical', String(summary.medical_miles), `$${summary.irs_rates.medical}/mile`, `$${summary.medical_deduction_value.toFixed(2)}`],
      ['Charity', String(summary.charity_miles), `$${summary.irs_rates.charity}/mile`, `$${summary.charity_deduction_value.toFixed(2)}`],
      ['Personal', String(summary.personal_miles), 'N/A', 'N/A'],
      ['Commute', String(summary.commute_miles), 'N/A (not deductible)', 'N/A'],
      ['Total', String(summary.total_miles), '', `$${(summary.business_deduction_value + summary.medical_deduction_value + summary.charity_deduction_value).toFixed(2)}`],
    ];
    downloadCsv(rows as string[][], `mileage-report-${summary.period.from}-${summary.period.to}.csv`);
  }

  function setReportPresetRange(preset: string) {
    setReportPreset(preset);
    const now = new Date();
    const year = now.getFullYear();
    if (preset === 'this-month') {
      setReportFrom(`${year}-${String(now.getMonth() + 1).padStart(2, '0')}-01`);
      setReportTo(todayStr());
    } else if (preset === 'last-month') {
      const lm = new Date(year, now.getMonth() - 1, 1);
      const lme = new Date(year, now.getMonth(), 0);
      setReportFrom(lm.toISOString().slice(0, 10));
      setReportTo(lme.toISOString().slice(0, 10));
    } else if (preset === 'ytd') {
      setReportFrom(`${year}-01-01`);
      setReportTo(todayStr());
    } else if (preset === 'last-year') {
      setReportFrom(`${year - 1}-01-01`);
      setReportTo(`${year - 1}-12-31`);
    }
  }

  const purposeChartData = summary
    ? [
        { name: 'Business', miles: summary.business_miles, color: PURPOSE_COLORS.business },
        { name: 'Personal', miles: summary.personal_miles, color: PURPOSE_COLORS.personal },
        { name: 'Commute', miles: summary.commute_miles, color: PURPOSE_COLORS.commute },
        { name: 'Medical', miles: summary.medical_miles, color: PURPOSE_COLORS.medical },
        { name: 'Charity', miles: summary.charity_miles, color: PURPOSE_COLORS.charity },
      ].filter(d => d.miles > 0)
    : [];

  return (
    <div className="space-y-6">
      {showLog && <LogTripForm onClose={() => setShowLog(false)} onSaved={handleSaved} />}
      {showImport && <ImportModal onClose={() => setShowImport(false)} onSaved={handleSaved} />}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Route className="h-6 w-6 text-emerald-500" />
          <h1 className="text-2xl font-bold text-zinc-100">Trip Logs</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowImport(true)}
            className="flex items-center gap-2 rounded-lg border border-zinc-700 px-3 py-2 text-sm text-zinc-300 hover:text-zinc-100 hover:border-zinc-500 transition-colors">
            <Upload className="h-4 w-4" /> Import CSV
          </button>
          <button onClick={() => setShowLog(true)}
            className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500 transition-colors">
            <Plus className="h-4 w-4" /> Log Trip
          </button>
        </div>
      </div>

      {/* Summary cards (this-period) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Miles', value: `${(monthSummary?.total_miles ?? 0).toLocaleString()}`, icon: Route, color: 'text-emerald-400' },
          { label: 'Business Miles', value: `${(monthSummary?.business_miles ?? 0).toLocaleString()}`, icon: Car, color: 'text-blue-400' },
          { label: 'Est. Deduction', value: `$${(monthSummary?.business_deduction_value ?? 0).toFixed(2)}`, icon: DollarSign, color: 'text-amber-400' },
          { label: 'Trips Logged', value: String(trips.length), icon: FileText, color: 'text-purple-400' },
        ].map(c => (
          <div key={c.label} className="rounded-xl border border-zinc-800 bg-zinc-900 p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-400">{c.label}</span>
              <c.icon className={`h-4 w-4 ${c.color}`} />
            </div>
            <p className="text-2xl font-semibold text-zinc-100">{c.value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="border-b border-zinc-800">
        <nav className="flex gap-6">
          {(['trips', 'mileage-report'] as const).map(t => (
            <button key={t} onClick={() => setActiveTab(t)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors capitalize ${
                activeTab === t ? 'border-emerald-500 text-emerald-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'
              }`}>
              {t === 'trips' ? 'Trip Log' : 'Mileage Report'}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Trip Log Tab ── */}
      {activeTab === 'trips' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-500">From</span>
              <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)}
                className="rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm text-zinc-200 focus:outline-none focus:ring-1 focus:ring-emerald-500" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-500">To</span>
              <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)}
                className="rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm text-zinc-200 focus:outline-none focus:ring-1 focus:ring-emerald-500" />
            </div>
            <select value={filterPurpose} onChange={e => setFilterPurpose(e.target.value)}
              className="rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm text-zinc-200 focus:outline-none focus:ring-1 focus:ring-emerald-500">
              <option value="">All purposes</option>
              <option value="business">Business</option>
              <option value="personal">Personal</option>
              <option value="commute">Commute</option>
              <option value="medical">Medical</option>
              <option value="charity">Charity</option>
            </select>
            <button onClick={exportTripsCsv}
              className="ml-auto flex items-center gap-2 rounded-lg border border-zinc-700 px-3 py-1.5 text-sm text-zinc-300 hover:text-zinc-100 hover:border-zinc-500 transition-colors">
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>

          {/* Table */}
          <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Vehicle</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Driver</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Route</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">Miles</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Purpose</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Billable</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {tripsLoading ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center">
                        <Loader2 className="h-5 w-5 animate-spin text-zinc-500 mx-auto" />
                      </td>
                    </tr>
                  ) : trips.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-zinc-500">
                        <Route className="h-8 w-8 mx-auto mb-2 text-zinc-700" />
                        No trips found. Log your first trip!
                      </td>
                    </tr>
                  ) : (
                    trips.map(t => (
                      <tr key={t.id} className="hover:bg-zinc-800/30 transition-colors">
                        <td className="px-4 py-3 text-zinc-300 whitespace-nowrap">{t.date}</td>
                        <td className="px-4 py-3 text-zinc-200">
                          <div className="flex items-center gap-1.5">
                            <Car className="h-3.5 w-3.5 text-zinc-500 flex-shrink-0" />
                            {t.vehicle_name}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-zinc-400">
                          {t.driver_name ? (
                            <div className="flex items-center gap-1.5">
                              <User className="h-3.5 w-3.5 text-zinc-500 flex-shrink-0" />
                              {t.driver_name}
                            </div>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-3 text-zinc-400 text-xs">
                          {(t.start_location || t.end_location) ? (
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3 text-zinc-600 flex-shrink-0" />
                              <span>{t.start_location ?? '?'} → {t.end_location ?? '?'}</span>
                            </div>
                          ) : (
                            t.business_purpose ? (
                              <span className="text-zinc-500 italic truncate max-w-xs block">{t.business_purpose}</span>
                            ) : '—'
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-zinc-200">{t.distance_miles.toFixed(1)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full border capitalize ${PURPOSE_STYLES[t.purpose]}`}>
                            {t.purpose}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {t.billable ? (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-full border bg-amber-500/10 text-amber-400 border-amber-500/30">
                              Billable
                            </span>
                          ) : (
                            <span className="text-zinc-600 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {tripData && tripData.total > tripData.data?.length && (
              <div className="px-4 py-3 border-t border-zinc-800 text-xs text-zinc-500">
                Showing {trips.length} of {tripData.total} trips
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Mileage Report Tab ── */}
      {activeTab === 'mileage-report' && (
        <div className="space-y-6">
          {/* Date range controls */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex gap-1">
              {(['this-month', 'last-month', 'ytd', 'last-year'] as const).map(p => (
                <button key={p} onClick={() => setReportPresetRange(p)}
                  className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                    reportPreset === p
                      ? 'bg-emerald-600 text-white'
                      : 'border border-zinc-700 text-zinc-400 hover:text-zinc-200'
                  }`}>
                  {p === 'this-month' ? 'This Month' : p === 'last-month' ? 'Last Month' : p === 'ytd' ? 'Year to Date' : 'Last Year'}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input type="date" value={reportFrom} onChange={e => { setReportFrom(e.target.value); setReportPreset('custom'); }}
                className="rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm text-zinc-200 focus:outline-none focus:ring-1 focus:ring-emerald-500" />
              <span className="text-zinc-500 text-sm">to</span>
              <input type="date" value={reportTo} onChange={e => { setReportTo(e.target.value); setReportPreset('custom'); }}
                className="rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm text-zinc-200 focus:outline-none focus:ring-1 focus:ring-emerald-500" />
            </div>
            <button onClick={exportMileageCsv} disabled={!summary}
              className="ml-auto flex items-center gap-2 rounded-lg border border-zinc-700 px-3 py-1.5 text-sm text-zinc-300 hover:text-zinc-100 hover:border-zinc-500 disabled:opacity-40 transition-colors">
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>

          {summaryLoading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />
            </div>
          ) : summary ? (
            <>
              {/* IRS rate notice */}
              <div className="flex items-center gap-2 rounded-lg border border-zinc-700 bg-zinc-800/50 px-4 py-3 text-sm text-zinc-400">
                <Calculator className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                Using {summary.irs_year} IRS standard mileage rates — Business: ${summary.irs_rates.business}/mile · Medical: ${summary.irs_rates.medical}/mile · Charity: ${summary.irs_rates.charity}/mile
              </div>

              {/* Deduction summary */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { label: 'Business Deduction', miles: summary.business_miles, value: summary.business_deduction_value, color: 'text-emerald-400', borderColor: 'border-emerald-500/20' },
                  { label: 'Medical Deduction', miles: summary.medical_miles, value: summary.medical_deduction_value, color: 'text-rose-400', borderColor: 'border-rose-500/20' },
                  { label: 'Charity Deduction', miles: summary.charity_miles, value: summary.charity_deduction_value, color: 'text-purple-400', borderColor: 'border-purple-500/20' },
                ].map(c => (
                  <div key={c.label} className={`rounded-xl border ${c.borderColor} bg-zinc-900 p-5`}>
                    <p className="text-xs text-zinc-500 mb-1">{c.label}</p>
                    <p className={`text-2xl font-bold ${c.color}`}>${c.value.toFixed(2)}</p>
                    <p className="text-xs text-zinc-500 mt-1">{c.miles.toLocaleString()} miles</p>
                  </div>
                ))}
              </div>

              {/* Full breakdown table */}
              <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
                <div className="px-4 py-3 border-b border-zinc-800">
                  <h3 className="text-sm font-semibold text-zinc-300">Mileage Breakdown</h3>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-800 bg-zinc-800/30">
                      <th className="px-4 py-2 text-left text-xs text-zinc-500">Category</th>
                      <th className="px-4 py-2 text-right text-xs text-zinc-500">Miles</th>
                      <th className="px-4 py-2 text-right text-xs text-zinc-500">% of Total</th>
                      <th className="px-4 py-2 text-right text-xs text-zinc-500">IRS Rate</th>
                      <th className="px-4 py-2 text-right text-xs text-zinc-500">Deduction</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    {[
                      { cat: 'Business', miles: summary.business_miles, rate: summary.irs_rates.business, deduction: summary.business_deduction_value, purpose: 'business' as TripPurpose },
                      { cat: 'Medical', miles: summary.medical_miles, rate: summary.irs_rates.medical, deduction: summary.medical_deduction_value, purpose: 'medical' as TripPurpose },
                      { cat: 'Charity', miles: summary.charity_miles, rate: summary.irs_rates.charity, deduction: summary.charity_deduction_value, purpose: 'charity' as TripPurpose },
                      { cat: 'Personal', miles: summary.personal_miles, rate: 0, deduction: 0, purpose: 'personal' as TripPurpose },
                      { cat: 'Commute', miles: summary.commute_miles, rate: 0, deduction: 0, purpose: 'commute' as TripPurpose },
                    ].map(r => (
                      <tr key={r.cat} className="hover:bg-zinc-800/20">
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full border ${PURPOSE_STYLES[r.purpose]}`}>
                            {r.cat}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-zinc-300">{r.miles.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-zinc-500">
                          {summary.total_miles > 0 ? `${Math.round((r.miles / summary.total_miles) * 100)}%` : '—'}
                        </td>
                        <td className="px-4 py-3 text-right text-zinc-500">
                          {r.rate > 0 ? `$${r.rate}/mi` : 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-zinc-200">
                          {r.deduction > 0 ? `$${r.deduction.toFixed(2)}` : '—'}
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-zinc-700 bg-zinc-800/30">
                      <td className="px-4 py-3 font-semibold text-zinc-100">Total</td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-zinc-100">{summary.total_miles.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-zinc-500">100%</td>
                      <td className="px-4 py-3" />
                      <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-400">
                        ${(summary.business_deduction_value + summary.medical_deduction_value + summary.charity_deduction_value).toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Chart */}
              {purposeChartData.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-5">
                    <h3 className="text-sm font-semibold text-zinc-300 mb-4">Miles by Purpose</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={purposeChartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                        <XAxis dataKey="name" tick={{ fill: '#71717a', fontSize: 11 }} />
                        <YAxis tick={{ fill: '#71717a', fontSize: 11 }} />
                        <Tooltip
                          contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 8 }}
                          labelStyle={{ color: '#e4e4e7' }}
                          formatter={(v: number) => [`${v.toLocaleString()} mi`, 'Miles']}
                        />
                        <Bar dataKey="miles" radius={[4, 4, 0, 0]}>
                          {purposeChartData.map((d, i) => (
                            <Cell key={i} fill={d.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* By vehicle */}
                  {summary.by_vehicle.length > 0 && (
                    <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-5">
                      <h3 className="text-sm font-semibold text-zinc-300 mb-4">Miles by Vehicle</h3>
                      <div className="space-y-2">
                        {summary.by_vehicle.slice(0, 6).map(v => (
                          <div key={v.vehicle_id} className="flex items-center justify-between">
                            <div className="flex items-center gap-2 min-w-0">
                              <Car className="h-3.5 w-3.5 text-zinc-500 flex-shrink-0" />
                              <span className="text-sm text-zinc-300 truncate">{v.name}</span>
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              <span className="text-xs text-zinc-500">{v.business_miles.toLocaleString()} biz</span>
                              <span className="text-sm font-mono text-zinc-200">{v.miles.toLocaleString()} mi</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-12 text-center">
              <Calculator className="w-8 h-8 mx-auto mb-2 text-zinc-600" />
              <p className="text-zinc-500">No trip data for this period</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Vehicle Trip History (used by VehicleDetailPage) ─────────

interface VehicleTripHistoryProps {
  vehicleId: string;
}

export function VehicleTripHistory({ vehicleId }: VehicleTripHistoryProps) {
  const [showLog, setShowLog] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading, refetch } = useApi<TripListResponse>(
    `/api/trips?vehicle_id=${vehicleId}&pageSize=100&${refreshKey}`,
  );
  const trips = data?.data ?? [];

  // Odometer data for chart
  const odometerData = trips
    .filter(t => t.end_odometer != null)
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(t => ({ date: t.date, odometer: t.end_odometer! }));

  const totalMiles = trips.reduce((s, t) => s + t.distance_miles, 0);
  const businessMiles = trips.filter(t => t.purpose === 'business').reduce((s, t) => s + t.distance_miles, 0);

  function handleSaved() {
    setRefreshKey(k => k + 1);
    refetch();
  }

  return (
    <div className="space-y-4">
      {showLog && (
        <LogTripForm
          vehicleId={vehicleId}
          vehicleName={trips[0]?.vehicle_name}
          onClose={() => setShowLog(false)}
          onSaved={handleSaved}
        />
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="text-center">
            <p className="text-xl font-bold text-zinc-100">{totalMiles.toLocaleString()}</p>
            <p className="text-xs text-zinc-500">Total miles</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-emerald-400">{businessMiles.toLocaleString()}</p>
            <p className="text-xs text-zinc-500">Business miles</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-zinc-300">{trips.length}</p>
            <p className="text-xs text-zinc-500">Trips</p>
          </div>
        </div>
        <button onClick={() => setShowLog(true)}
          className="flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-500 transition-colors">
          <Plus className="h-3.5 w-3.5" /> Log Trip
        </button>
      </div>

      {/* Odometer timeline (if data) */}
      {odometerData.length >= 2 && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-4">
          <h4 className="text-xs font-medium text-zinc-500 uppercase tracking-wide mb-3">Odometer History</h4>
          <ResponsiveContainer width="100%" height={120}>
            <BarChart data={odometerData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
              <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 10 }} />
              <YAxis tick={{ fill: '#71717a', fontSize: 10 }} />
              <Tooltip
                contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 8 }}
                labelStyle={{ color: '#e4e4e7' }}
                formatter={(v: number) => [`${v.toLocaleString()} mi`, 'Odometer']}
              />
              <Bar dataKey="odometer" fill="#10b981" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Trip table */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Driver</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-zinc-500 uppercase">Miles</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Purpose</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {isLoading ? (
                <tr><td colSpan={5} className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-500 mx-auto" /></td></tr>
              ) : trips.length === 0 ? (
                <tr><td colSpan={5} className="py-8 text-center text-zinc-500 text-sm">No trips recorded for this vehicle</td></tr>
              ) : (
                trips.map(t => (
                  <>
                    <tr key={t.id} className="hover:bg-zinc-800/30 cursor-pointer" onClick={() => setExpanded(expanded === t.id ? null : t.id)}>
                      <td className="px-4 py-3 text-zinc-300">{t.date}</td>
                      <td className="px-4 py-3 text-zinc-400">{t.driver_name ?? '—'}</td>
                      <td className="px-4 py-3 text-right font-mono text-zinc-200">{t.distance_miles.toFixed(1)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full border capitalize ${PURPOSE_STYLES[t.purpose]}`}>
                          {t.purpose}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-zinc-500 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="truncate max-w-[160px]">
                            {t.business_purpose ?? (t.start_location ? `${t.start_location} → ${t.end_location ?? '?'}` : '—')}
                          </span>
                          {expanded === t.id ? <ChevronUp className="h-3 w-3 flex-shrink-0 ml-2" /> : <ChevronDown className="h-3 w-3 flex-shrink-0 ml-2" />}
                        </div>
                      </td>
                    </tr>
                    {expanded === t.id && (
                      <tr key={`${t.id}-detail`} className="bg-zinc-800/20">
                        <td colSpan={5} className="px-4 py-3">
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                            {t.start_location && <div><span className="text-zinc-500">From</span><p className="text-zinc-300 mt-0.5">{t.start_location}</p></div>}
                            {t.end_location && <div><span className="text-zinc-500">To</span><p className="text-zinc-300 mt-0.5">{t.end_location}</p></div>}
                            {t.start_odometer && <div><span className="text-zinc-500">Start Odo</span><p className="text-zinc-300 mt-0.5">{t.start_odometer.toLocaleString()} mi</p></div>}
                            {t.end_odometer && <div><span className="text-zinc-500">End Odo</span><p className="text-zinc-300 mt-0.5">{t.end_odometer.toLocaleString()} mi</p></div>}
                            {t.business_purpose && <div className="col-span-2"><span className="text-zinc-500">Business Purpose</span><p className="text-zinc-300 mt-0.5">{t.business_purpose}</p></div>}
                            {t.notes && <div className="col-span-2"><span className="text-zinc-500">Notes</span><p className="text-zinc-300 mt-0.5">{t.notes}</p></div>}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
